import { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';
import { apiService } from '../services/api';

interface SessionTimerProps {
  className?: string;
}

export function SessionTimer({ className = '' }: SessionTimerProps) {
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [isExpiringSoon, setIsExpiringSoon] = useState(false);

  useEffect(() => {
    const updateTimer = () => {
      // Access-Token liegt nur in-memory im ApiService (F-9, kein localStorage).
      const token = apiService.getAccessToken();
      if (!token) {
        setTimeRemaining(null);
        return;
      }

      try {
        // Decode JWT token (simple base64 decode of payload)
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expirationTime = payload.exp * 1000; // Convert to milliseconds
        const currentTime = Date.now();
        const remaining = Math.max(0, expirationTime - currentTime);

        setTimeRemaining(remaining);
        setIsExpiringSoon(remaining < 5 * 60 * 1000); // Less than 5 minutes
      } catch (error) {
        console.error('Failed to decode token', error);
        setTimeRemaining(null);
      }
    };

    // Update immediately
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, []);

  if (timeRemaining === null) {
    return null;
  }

  const formatTime = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getColorClasses = () => {
    if (timeRemaining < 60 * 1000) {
      // Less than 1 minute - red
      return 'text-red-600 bg-red-50 border-red-200';
    } else if (isExpiringSoon) {
      // Less than 5 minutes - yellow
      return 'text-yellow-700 bg-yellow-50 border-yellow-200';
    }
    // Normal - gray
    return 'text-gray-600 bg-gray-50 border-gray-200';
  };

  return (
    <div
      className={`flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm font-medium ${getColorClasses()} ${className}`}
      title="Verbleibende Sitzungszeit"
    >
      <Clock className="w-4 h-4" />
      <span className="font-mono">{formatTime(timeRemaining)}</span>
    </div>
  );
}
